import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import { TILE_LABEL, toDigitTiles } from '../lib/playTiles';
import type { SubmitEntryResponse } from '../api/types';

interface Props {
  puzzleNumber: number;
  /** Spieltag (YYYY-MM-DD) zur datums-genauen Auflösung der Nummer —
   *  Pastpuzzle wiederholt Nummern. */
  puzzleDate?: string;
  onSubmitted(response: SubmitEntryResponse): void;
}

interface PlayState {
  puzzle_number: number;
  puzzle_date: string;
  title: string | null;
  hint_1: string;
  hint_2: string;
  hint_3: string;
  hint_4: string;
}

interface AttemptResult {
  guess: number;
  emojis: string;
  solved: boolean;
}

type Status = 'loading' | 'error' | 'playing' | 'finishing' | 'done';

const MAX_ATTEMPTS = 4;

export function PlayInline({ puzzleNumber, puzzleDate, onSubmitted }: Props) {
  const dateQuery = puzzleDate ? `?date=${puzzleDate}` : '';
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [state, setState] = useState<PlayState | null>(null);
  const [attempts, setAttempts] = useState<AttemptResult[]>([]);
  const [guess, setGuess] = useState('');
  const [submittingGuess, setSubmittingGuess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setStatus('loading');
      setErrorMsg(null);
      try {
        const { data } = await api.get<PlayState>(`/play/state/${puzzleNumber}${dateQuery}`);
        if (cancelled) return;
        setState(data);
        setStatus('playing');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        if (axios.isAxiosError(err) && err.response?.data) {
          const data = err.response.data as { error?: string };
          setErrorMsg(data.error ?? 'Tippen aktuell nicht möglich');
        } else {
          setErrorMsg('Tippen aktuell nicht möglich');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [puzzleNumber, dateQuery]);

  // Fokus auf das Input-Feld setzen, sobald wir spielen können oder
  // gerade ein Versuch verbucht wurde — fühlt sich auf dem Handy
  // schnell an, kein Extra-Tap nötig.
  useEffect(() => {
    if (status === 'playing' && !submittingGuess) {
      inputRef.current?.focus();
    }
  }, [status, submittingGuess, attempts.length]);

  const visibleHints = useMemo(() => {
    if (!state) return [] as string[];
    const wrong = attempts.filter((a) => !a.solved).length;
    const all = [state.hint_1, state.hint_2, state.hint_3, state.hint_4];
    // 0 falsche Versuche → nur Hinweis 1, 1 falscher → +Hinweis 2 …
    const visible = Math.min(MAX_ATTEMPTS, wrong + 1);
    return all.slice(0, visible);
  }, [state, attempts]);

  const previousGuesses = useMemo(
    () => new Set(attempts.map((a) => a.guess)),
    [attempts],
  );
  const gameOver = attempts.some((a) => a.solved) || attempts.length >= MAX_ATTEMPTS;

  const submitGuess = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setErrorMsg(null);
    if (!state) return;
    const trimmed = guess.trim();
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || trimmed.length === 0) {
      setErrorMsg('Bitte eine Jahreszahl eingeben');
      return;
    }
    if (parsed <= 0) {
      setErrorMsg('Bitte eine positive Jahreszahl eingeben');
      return;
    }
    if (previousGuesses.has(parsed)) {
      setErrorMsg('Diese Jahreszahl hast du schon getippt');
      return;
    }

    setSubmittingGuess(true);
    try {
      const { data } = await api.post<{ emojis: string; solved: boolean }>(
        '/play/guess',
        { puzzle_number: state.puzzle_number, guess: parsed, puzzle_date: puzzleDate },
      );
      const nextAttempts: AttemptResult[] = [
        ...attempts,
        { guess: parsed, emojis: data.emojis, solved: data.solved },
      ];
      setAttempts(nextAttempts);
      setGuess('');

      const finished = data.solved || nextAttempts.length >= MAX_ATTEMPTS;
      if (finished) {
        await finishGame(state.puzzle_number, nextAttempts);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const data = err.response.data as { error?: string };
        setErrorMsg(data.error ?? 'Tipp konnte nicht gespeichert werden');
      } else {
        setErrorMsg('Tipp konnte nicht gespeichert werden');
      }
    } finally {
      setSubmittingGuess(false);
    }
  };

  const finishGame = async (puzzleNum: number, all: AttemptResult[]): Promise<void> => {
    setStatus('finishing');
    try {
      const { data } = await api.post<SubmitEntryResponse>('/play/finish', {
        puzzle_number: puzzleNum,
        guesses: all.map((a) => a.guess),
        puzzle_date: puzzleDate,
      });
      setStatus('done');
      onSubmitted(data);
    } catch (err) {
      setStatus('playing');
      if (axios.isAxiosError(err) && err.response?.data) {
        const data = err.response.data as { error?: string };
        setErrorMsg(data.error ?? 'Spiel konnte nicht gespeichert werden');
      } else {
        setErrorMsg('Spiel konnte nicht gespeichert werden');
      }
    }
  };

  if (status === 'loading') {
    return <p className="card__text">Lade Spielzustand …</p>;
  }
  if (status === 'error') {
    return (
      <p className="entry-form__error" role="alert">
        {errorMsg ?? 'Tippen aktuell nicht möglich'}
      </p>
    );
  }
  if (!state) return null;

  return (
    <div className="play">
      <ol className="play__hints">
        {visibleHints.map((h, idx) => (
          <li key={idx} className="play__hint">
            <span className="play__hint-num">Hinweis {idx + 1}</span>
            <span className="play__hint-text">{h}</span>
          </li>
        ))}
      </ol>

      {attempts.length > 0 && (
        <ol className="play__attempts">
          {attempts.map((a, idx) => (
            <li key={idx} className="play__attempt">
              <span className="play__attempt-num">{idx + 1}</span>
              <span className="play__tiles">
                {toDigitTiles(a.guess, a.emojis).map((tile, i) => (
                  <span key={i} className={`play__tile play__tile--${tile.state}`}>
                    {tile.digit}
                    <span className="sr-only"> {TILE_LABEL[tile.state]}</span>
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ol>
      )}

      {!gameOver && status === 'playing' && (
        <form className="play__form" onSubmit={submitGuess}>
          <label className="field">
            <span className="field__label">
              Versuch {attempts.length + 1} von {MAX_ATTEMPTS}
            </span>
            <input
              ref={inputRef}
              className="field__input"
              type="number"
              inputMode="numeric"
              placeholder="z.B. 1972"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              disabled={submittingGuess}
              required
            />
          </label>
          {errorMsg && (
            <p className="entry-form__error" role="alert">{errorMsg}</p>
          )}
          <button
            type="submit"
            className="button button--primary"
            disabled={submittingGuess}
          >
            {submittingGuess ? 'Wird geprüft …' : 'Tippen'}
          </button>
        </form>
      )}

      {status === 'finishing' && (
        <p className="card__text">Speichere Ergebnis …</p>
      )}
      {status === 'done' && (
        <p className="today__just-submitted">✔️ Eingetragen.</p>
      )}
    </div>
  );
}
